// External Imports
import { useContext, useRef, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import CustomerSelector from "../customers-page/CustomerSelector";
import Modal from "../UI/Modal";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";

// Categories of payments
const categories = [
  "booking",
  "subscription",
  "deposit",
  "damage",
  "insurance",
  "tolls",
  "ticket",
  "miles",
  "violation",
  "charging",
  "other",
];

/** ========================================================================
 * * InvoiceModal Component
 * This component renders a modal with a form to create an invoice.
 * ========================================================================== */

export default function InvoiceModal({
  open,
  setOpen,
  customerId,
  bookingId,
  amount,
  description,
  category,
  onComplete,
}) {
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState({});
  const taxesRef = useRef();
  const subtotalRef = useRef();
  const totalRef = useRef();

  // =================== TOTAL HANDLER ===================
  const totalHandler = () => {
    const taxes = parseFloat(taxesRef.current.value);
    const subtotal = parseFloat(subtotalRef.current.value);
    totalRef.current.value = (subtotal + taxes).toFixed(2);
  };

  // =================== SUBMIT PAYMENT HANDLER ===================
  const submitHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        customerId: customer.id,
        bookingId: document.getElementById("bookingId").value,
        subtotal: subtotalRef.current.value,
        taxes: taxesRef.current.value,
        description: document.getElementById("description").value,
        daysUntilDue: document.getElementById("daysUntilDue").value,
        category: document.getElementById("category").value,
      };

      // Check if amount is valid
      if (parseFloat(totalRef.current?.value) <= 0)
        throw new Error("Really? You wanna send an invoice for $0 dollars? 🤨");

      // Send Request
      const paymentId = await requestAdminAPI("send-invoice", payload);

      // Navigate to payments
      if (onComplete) onComplete(paymentId);
      else navigate("/payments/info?id=" + paymentId);
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal p-6 text-stone-200 lg:w-[45vw]">
        <Heading
          title="Send Invoice"
          text="In order to charge a customer, an invoice must be created first. Invoices can be charged by visiting the payment information page."
          isBackArrow={false}
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <div className="grid gap-8 text-sm">
          <div className="grid gap-4 lg:grid-cols-3">
            <CustomerSelector onSelect={setCustomer} selected={customerId} />

            <DetailPair
              label="Associated Booking ID"
              edit={true}
              inputOptions={{
                type: "text",
                id: "bookingId",
                defaultValue: bookingId || "",
              }}
            />

            <SelectPair
              label="Category"
              edit={true}
              options={categories}
              inputOptions={{
                required: true,
                id: "category",
                defaultValue: category || "other",
              }}
            />

            <DetailPair
              label="Days Until Due"
              edit={true}
              inputOptions={{
                id: "daysUntilDue",
                type: "number",
                min: "0",
                required: true,
                defaultValue: "3",
              }}
            />

            <DetailPair
              label="Taxes"
              edit={true}
              inputOptions={{
                type: "number",
                id: "taxes",
                step: "0.01",
                min: "0",
                required: true,
                defaultValue: 0,
                ref: taxesRef,
                onChange: totalHandler,
              }}
            />

            <DetailPair
              label="Subtotal"
              edit={true}
              inputOptions={{
                type: "number",
                id: "subtotal",
                step: "0.01",
                min: "0",
                required: true,
                defaultValue: amount || "",
                ref: subtotalRef,
                onChange: totalHandler,
              }}
            />

            <DetailPair
              label="Total"
              edit={true}
              inputOptions={{
                type: "number",
                id: "amount",
                disabled: true,
                defaultValue: amount || "",
                ref: totalRef,
              }}
            />

            <div className="lg:col-span-2">
              <DetailPair
                label="Description"
                edit={true}
                inputOptions={{
                  type: "text",
                  id: "description",
                  required: true,
                  defaultValue: description || "",
                }}
              />
            </div>
          </div>

          {loading ? (
            <LoadingSpinnerWrapper />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <div onClick={() => setOpen(false)} className="btn-secondary">
                Cancel
              </div>
              <button onClick={submitHandler} className="btn-primary">
                Confirm
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
